// src/pages/msme/SigmaStudio7Mudas.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faPlus,
  faDownload,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";

// Realistic examples of 7 Mudas in Flexo / Adhesive Label Printing Factory
const exampleMudas = [
  {
    id: 1,
    wasteType: "Overproduction",
    description: "Producing more than customer demand or producing too early",
    example: "Printing extra labels 'just in case' or running large batches without confirmed orders",
    impact: "Excess inventory, tied-up capital, risk of obsolescence, storage costs",
    countermeasure: "Implement pull system, smaller batch sizes, better forecasting with customers",
  },
  {
    id: 2,
    wasteType: "Waiting",
    description: "Idle time when no value-adding activity occurs",
    example: "Press waiting for ink mixing, plate making delay, or material arrival",
    impact: "Long lead times, poor OEE, delayed deliveries to customers",
    countermeasure: "SMED for faster changeovers, cross-training, synchronized processes",
  },
  {
    id: 3,
    wasteType: "Transportation",
    description: "Unnecessary movement of materials or products",
    example: "Moving rolls between distant storage areas multiple times, unnecessary trips between ink room and press",
    impact: "Risk of damage, extra handling labor, time loss",
    countermeasure: "Layout optimization, point-of-use storage, milk-run systems",
  },
  {
    id: 4,
    wasteType: "Over-processing",
    description: "Doing more work or using more resources than required by customer",
    example: "Excessive color proofs, over-polishing plates, or applying extra varnish layers not specified",
    impact: "Higher costs, longer cycle time, unnecessary consumables usage",
    countermeasure: "Clear customer specifications, standardized work, value stream mapping",
  },
  {
    id: 5,
    wasteType: "Inventory",
    description: "Excess stock of raw materials, WIP or finished goods",
    example: "Large stock of substrate rolls, finished label rolls waiting for dispatch",
    impact: "Capital tied up, risk of damage/expiry, space consumption",
    countermeasure: "Just-in-time purchasing, kanban for WIP control, ABC analysis",
  },
  {
    id: 6,
    wasteType: "Motion",
    description: "Unnecessary movement by people (bending, walking, reaching)",
    example: "Operator walking long distances to fetch anilox sleeves or tools, searching for misplaced dies",
    impact: "Fatigue, lower productivity, higher injury risk",
    countermeasure: "5S workplace organization, shadow boards, ergonomic workstations",
  },
  {
    id: 7,
    wasteType: "Defects / Rework",
    description: "Producing defective products or requiring corrections",
    example: "Misregistration, color mismatch, die-cut errors requiring reprint or manual correction",
    impact: "Scrap, rework labor, customer complaints, lost reputation",
    countermeasure: "Poka-yoke, inline vision inspection, root cause analysis (5 Whys)",
  },
];

// Color map for waste types (visual distinction)
const wasteColors = {
  Overproduction: "bg-red-700",
  Waiting: "bg-orange-600",
  Transportation: "bg-amber-600",
  Overprocessing: "bg-yellow-600",
  Inventory: "bg-lime-700",
  Motion: "bg-teal-700",
  Defects: "bg-pink-700",
};

const SigmaStudio7Mudas = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showWhyModal, setShowWhyModal] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [mudas, setMudas] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const initialFormData = {
    wasteType: "",
    description: "",
    example: "",
    impact: "",
    countermeasure: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const handlePopulate = () => {
    if (mudas.length > 0) {
      if (!window.confirm("This will replace current entries with example 7 Mudas from label printing. Continue?")) {
        return;
      }
    }
    setMudas(exampleMudas.map(m => ({ ...m })));
    setCurrentPage(1);
    setSearch("");
  };

  const handleWhyMuda = () => {
    setShowWhyModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this waste entry?")) {
      setMudas((prev) => prev.filter((m) => m.id !== id));
    }
  };

  const handleEdit = (id) => {
    const muda = mudas.find((m) => m.id === id);
    if (muda) {
      setFormData({ ...initialFormData, ...muda });
      setEditingId(id);
      setShowModal(true);
    }
  };

  const handleAdd = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newMuda = {
      id: editingId || Math.max(0, ...mudas.map((m) => m.id)) + 1,
      ...formData,
    };
    if (editingId) {
      setMudas((prev) =>
        prev.map((m) => (m.id === editingId ? newMuda : m))
      );
    } else {
      setMudas((prev) => [...prev, newMuda]);
    }
    setShowModal(false);
    setFormData(initialFormData);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData(initialFormData);
  };

  const handleExport = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      ["Waste Type,Description,Example in Printing,Impact,Countermeasure"]
        .concat(
          mudas.map(
            (m) =>
              `"${m.wasteType.replace(/"/g, '""')}","${m.description.replace(/"/g, '""')}","${m.example.replace(/"/g, '""')}","${m.impact.replace(/"/g, '""')}","${m.countermeasure.replace(/"/g, '""')}"`
          )
        )
        .join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "7_mudas_label_printing.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtering & Pagination
  const filtered = mudas.filter((m) =>
    m.wasteType.toLowerCase().includes(search.toLowerCase()) ||
    m.description.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            🎛️ Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">
            7 Mudas Waste Identification Playground
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">
                {userName}
              </div>
              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm"
            >
              🔐
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm">{userName}</div>
              <button onClick={handleLogout} className="p-2 rounded-md bg-red-600">
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">🔐</Link>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-row items-center justify-between mb-4 gap-2">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 place-items-center mt-4">
          <button
            onClick={handlePopulate}
            className="text-xs lg:text-sm w-32 lg:w-48 px-1 py-1 bg-green-600 hover:bg-green-700 rounded"
          >
            <FontAwesomeIcon icon={faPlus} /> Populate Examples
          </button>
          <button
            onClick={handleWhyMuda}
            className="text-xs lg:text-sm w-32 lg:w-40 px-1 py-1 bg-green-600 hover:bg-green-700 rounded"
          >
            <FontAwesomeIcon icon={faQuestionCircle} /> Why 7 Mudas?
          </button>
          <button
            onClick={handleAdd}
            className="text-xs lg:text-sm w-32 lg:w-40 px-1 py-1 bg-green-600 hover:bg-green-700 rounded"
          >
            <FontAwesomeIcon icon={faPlus} /> Add Waste
          </button>
          <button
            onClick={handleExport}
            className="text-xs lg:text-sm w-32 lg:w-40 px-1 py-1 bg-blue-600 hover:bg-blue-700 rounded"
          >
            <FontAwesomeIcon icon={faDownload} /> Export CSV
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search waste type or description..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full sm:w-1/2 px-3 py-2 rounded bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Desktop Table */}
      <div className="min-h-[40vh] hidden sm:block overflow-x-auto">
        <table className="min-w-full border border-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-3 py-2 text-left border-b border-gray-700">Waste Type</th>
              <th className="px-3 py-2 text-left border-b border-gray-700">Description</th>
              <th className="px-3 py-2 text-left border-b border-gray-700">Example in Printing</th>
              <th className="px-3 py-2 text-left border-b border-gray-700">Impact</th>
              <th className="px-3 py-2 text-left border-b border-gray-700">Countermeasure</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((m) => (
              <tr key={m.id} className="hover:bg-black">
                <td className="px-3 py-2 border-b border-gray-700">
                  <span className={`px-2 py-1 rounded text-xs ${wasteColors[m.wasteType] || "bg-gray-600"}`}>
                    {m.wasteType}
                  </span>
                </td>
                <td className="px-3 py-2 border-b border-gray-700">{m.description}</td>
                <td className="px-3 py-2 border-b border-gray-700">{m.example}</td>
                <td className="px-3 py-2 border-b border-gray-700">{m.impact}</td>
                <td className="px-3 py-2 border-b border-gray-700">{m.countermeasure}</td>
                <td className="px-3 py-2 border-b border-gray-700 flex justify-center gap-2">
                  <button
                    onClick={() => handleEdit(m.id)}
                    className="w-8 p-1 bg-yellow-600 hover:bg-yellow-700 rounded"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleDelete(m.id)}
                    className="w-8 p-1 bg-red-600 hover:bg-red-700 rounded"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td colSpan="6" className="px-3 py-4 text-center text-gray-400">
                  No wastes recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden mt-4 space-y-4">
        {paginated.map((m) => (
          <div
            key={m.id}
            className="bg-gray-800 p-4 rounded shadow border border-gray-700"
          >
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-bold">
                <span className={`px-2 py-1 rounded text-sm ${wasteColors[m.wasteType] || "bg-gray-600"}`}>
                  {m.wasteType}
                </span>
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(m.id)}
                  className="p-1 bg-yellow-600 hover:bg-yellow-700 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={() => handleDelete(m.id)}
                  className="p-1 bg-red-600 hover:bg-red-700 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            <p><span className="font-semibold">Description:</span> {m.description}</p>
            <p><span className="font-semibold">Example:</span> {m.example}</p>
            <p><span className="font-semibold">Impact:</span> {m.impact}</p>
            <p><span className="font-semibold">Countermeasure:</span> {m.countermeasure}</p>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">
                {editingId ? "Edit Waste Entry" : "Add Waste (Muda)"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Waste Type</label>
                  <input
                    type="text"
                    value={formData.wasteType}
                    onChange={(e) => setFormData({ ...formData, wasteType: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Example in Label Printing</label>
                  <textarea
                    value={formData.example}
                    onChange={(e) => setFormData({ ...formData, example: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Impact</label>
                  <textarea
                    value={formData.impact}
                    onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Possible Countermeasure</label>
                  <textarea
                    value={formData.countermeasure}
                    onChange={(e) => setFormData({ ...formData, countermeasure: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                  />
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded"
                  >
                    {editingId ? "Update" : "Add"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Why 7 Mudas Modal */}
      {showWhyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-yellow-200">
                Why identify the 7 Mudas in Label Printing & Packaging?
              </h2>
              <button
                onClick={() => setShowWhyModal(false)}
                className="text-gray-400 hover:text-white text-2xl leading-none"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-4 text-sm leading-relaxed">
              <p>
                In fast-moving label & flexible packaging MSMEs, hidden wastes eat profits daily:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Short runs, frequent changeovers, high material costs</li>
                <li>Small teams wearing multiple hats</li>
                <li>Customer pressure for quick delivery & zero defects</li>
              </ul>

              <p className="font-medium mt-4">Common problems when mudas are ignored:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-red-300">
                <li>Excess inventory blocks cash flow</li>
                <li>Waiting time kills machine efficiency</li>
                <li>Defects cause expensive reprints & lost customers</li>
              </ul>

              <p className="font-medium mt-4">The classic 7 Mudas (from Toyota Production System):</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {Object.keys(wasteColors).map((type) => (
                  <div key={type} className="bg-gray-700 p-3 rounded">
                    <span className={`font-bold ${wasteColors[type]}`}>{type}</span>
                  </div>
                ))}
              </div>

              <p className="mt-4 text-teal-300 font-medium">
                Identifying & eliminating these wastes → higher OEE, lower costs, faster delivery, better margins, calmer operations.
              </p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowWhyModal(false)}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-center items-center gap-2 mt-6">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faChevronLeft} /> Prev
        </button>
        <span>Page {currentPage} / {totalPages || 1}</span>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-12">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudio7Mudas;